import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import PropertyCard from '../components/PropertyCard';
import GrowthAnalysisPanel from '../components/GrowthAnalysisPanel';
import { Button } from '../components/ui/button';
import { ArrowRight, Sparkles, MapPin, TrendingUp, Home as HomeIcon } from 'lucide-react';
import { useSEO, buildBreadcrumbSchema, buildFaqSchema } from '../utils/seo';
import { findCity, citiesByCountry } from '../utils/seoCities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Auto-generated SEO landing page — one URL per major city.
 *
 * /properties-in/london, /properties-in/hyderabad, /properties-in/toronto, etc.
 *
 * Pulls live listings for that city, ships RealEstateListing + Place + FAQ +
 * Breadcrumb schema, and cross-links to neighbouring cities for healthy
 * internal-link velocity.
 */
const LocationLandingPage = () => {
  const { slug } = useParams();
  const city = findCity(slug || '');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ count: 0, avgPrice: 0, minPrice: 0, maxPrice: 0 });
  const [guide, setGuide] = useState(null);

  useEffect(() => {
    if (!city) return;
    const guideSlug = `buying-property-${city.slug}-2026`;
    axios
      .get(`${API_URL}/api/blog/posts/${guideSlug}`)
      .then((r) => {
        if (r.data && r.data.published) setGuide(r.data);
      })
      .catch(() => setGuide(null));
  }, [city]);

  useEffect(() => {
    if (!city) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/properties`, {
          params: { city: city.name, country: city.country, limit: 12, status: 'active' },
        });
        if (cancelled) return;
        const items = r.data || [];
        setListings(items);
        if (items.length) {
          const prices = items.map((p) => p.price).filter((p) => typeof p === 'number');
          setStats({
            count: items.length,
            avgPrice: prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : 0,
            minPrice: prices.length ? Math.min(...prices) : 0,
            maxPrice: prices.length ? Math.max(...prices) : 0,
          });
        }
      } catch (err) {
        // Empty state is fine for non-existent / yet-to-launch cities, but log
        // the error so silent infra failures don't go unnoticed in monitoring.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[LocationLandingPage] city listings fetch failed:', err?.message || err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [city]);

  // Always call hooks in the same order — useSEO must run regardless of city existence
  const seoTitle = city
    ? `Property in ${city.name} — Houses & Flats for Sale & Rent | Shopperden`
    : 'Property by Location — Shopperden';
  const seoDesc = city
    ? `Browse direct-from-owner property in ${city.name}, ${city.countryName}. ${city.tagline}. Real listings, AI valuations, no estate-agent commission. Buy, sell, rent on Shopperden.`
    : '';
  const canonical = typeof window !== 'undefined' ? window.location.href : '';

  const faqItems = city ? [
    { q: `How many properties are listed in ${city.name} on Shopperden?`, a: `Shopperden currently has ${stats.count || 'a growing portfolio of'} verified property listings in ${city.name}. New listings go live daily directly from owners and approved agents — no inventory padding.` },
    { q: `What does the average property in ${city.name} cost on Shopperden?`, a: stats.avgPrice ? `Across our current ${city.name} listings, the average asking price is ${city.currency} ${stats.avgPrice.toLocaleString('en-GB')}, ranging from ${city.currency} ${stats.minPrice.toLocaleString('en-GB')} to ${city.currency} ${stats.maxPrice.toLocaleString('en-GB')}.` : `Prices in ${city.name} vary widely by area and property type. Use Shopperden's AI valuation on any listing for an instant comparable-driven estimate.` },
    { q: `Is Shopperden cheaper than Rightmove / 99acres / Realtor.ca for ${city.name}?`, a: `For sellers and landlords, yes — Shopperden charges no listing fees during the trial. For buyers, you pay a small one-time introduction fee instead of inflated estate-agent commissions.` },
    { q: `Can I list a plot or land in ${city.name}?`, a: `Yes. Shopperden has a dedicated plot/land listing flow with country-specific approval fields (HMDA / DTCP / RERA in India, planning permission in the UK, zoning in Canada).` },
    { q: `Are listings in ${city.name} verified?`, a: `Listings from agents pass ID + KYC verification. Direct sellers go through a lighter verification — but every listing is screened against our AML and sanctions feeds before it goes live.` },
  ] : [];

  const schemas = city ? [
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Locations', url: '/locations' },
      { name: city.countryName, url: `/locations#${city.country.toLowerCase()}` },
      { name: city.name, url: `/properties-in/${city.slug}` },
    ]),
    buildFaqSchema(faqItems),
    {
      '@context': 'https://schema.org',
      '@type': 'Place',
      name: city.name,
      address: { '@type': 'PostalAddress', addressLocality: city.name, addressCountry: city.country },
      url: `https://shopperden.com/properties-in/${city.slug}`,
    },
    listings.length ? {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Property listings in ${city.name}`,
      numberOfItems: listings.length,
      itemListElement: listings.slice(0, 10).map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://shopperden.com/property/${p.id || p._id}`,
        name: p.title,
      })),
    } : null,
  ].filter(Boolean) : null;

  useSEO({
    title: seoTitle,
    description: seoDesc,
    canonical,
    schema: schemas,
  });

  if (!city) return <Navigate to="/locations" replace />;

  const sameCountry = citiesByCountry(city.country).filter((c) => c.slug !== city.slug).slice(0, 6);

  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="location-landing-page">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F766E] text-white overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20">
          <nav className="text-xs text-white/60 mb-4 tracking-wider" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/locations" className="hover:text-white">Locations</Link>
            <span className="mx-2">›</span>
            <span className="text-white">{city.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 mb-5 backdrop-blur-sm">
            <MapPin className="h-3 w-3 text-[#FED7AA]" />
            <p className="text-[11px] tracking-[0.28em] uppercase text-[#FED7AA] font-bold">{city.countryName}</p>
          </div>
          <h1 className="font-['Playfair_Display'] font-semibold leading-[1.05] tracking-tight text-[2.25rem] sm:text-5xl lg:text-[4rem]">
            <span className="block">Property in</span>
            <span className="block text-[#FED7AA] italic">{city.name}</span>
          </h1>
          <p className="mt-5 text-white/75 text-[15px] sm:text-lg leading-relaxed max-w-2xl">
            {city.tagline}. Browse direct-from-owner houses, flats, plots and commercial property — verified listings, AI valuations, no estate-agent commission.
          </p>
          <p className="mt-3 text-white/50 text-sm">Popular areas: <span className="text-white/80">{city.popPick}</span></p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/properties" data-testid="loc-cta-browse">
              <Button className="bg-[#E07A3A] hover:bg-[#C4652E] text-white tracking-widest uppercase font-bold">
                Browse all listings <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/register?role=seller" data-testid="loc-cta-list">
              <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/15 tracking-widest uppercase font-bold">
                List your {city.name} property
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      {stats.count > 0 && (
        <section className="bg-white border-y border-[#E2E8F0]">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat icon={HomeIcon} label="Live listings" value={stats.count} />
            <Stat icon={TrendingUp} label="Avg asking" value={`${city.currency} ${stats.avgPrice.toLocaleString('en-GB')}`} />
            <Stat icon={Sparkles} label="From" value={`${city.currency} ${stats.minPrice.toLocaleString('en-GB')}`} />
            <Stat icon={Sparkles} label="Up to" value={`${city.currency} ${stats.maxPrice.toLocaleString('en-GB')}`} />
          </div>
        </section>
      )}

      {/* Listings grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-12 lg:py-16">
        <GrowthAnalysisPanel slug={city.slug} />

        <div className="flex items-end justify-between mb-7">
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A]">
            Featured listings in {city.name}
          </h2>
          <Link to="/properties" className="text-sm text-[#E07A3A] hover:text-[#C4652E] tracking-wider uppercase font-semibold">
            View all <ArrowRight className="h-4 w-4 inline-block ml-1" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={`skel-${i}`} className="aspect-[4/5] bg-white border border-[#E2E8F0] animate-pulse" />)}
          </div>
        ) : listings.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E2E8F0] p-12 text-center">
            <p className="text-[#475569] mb-4">No live listings in {city.name} yet — be the first.</p>
            <Link to="/register?role=seller">
              <Button className="bg-[#E07A3A] hover:bg-[#C4652E] text-white tracking-widest uppercase font-bold">
                List your {city.name} property
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((p) => <PropertyCard key={p.id || p._id} property={p} />)}
          </div>
        )}
      </section>

      {/* Cross-link to nearby cities — internal-link velocity for SEO */}
      {sameCountry.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-12">
          <h3 className="text-xs tracking-[0.3em] uppercase text-[#94A3B8] font-semibold mb-4">More cities in {city.countryName}</h3>
          <div className="flex flex-wrap gap-2">
            {sameCountry.map((c) => (
              <Link
                key={c.slug}
                to={`/properties-in/${c.slug}`}
                className="px-4 py-2 bg-white border border-[#E2E8F0] hover:border-[#E07A3A] hover:text-[#E07A3A] text-sm tracking-wider transition-colors"
                data-testid={`cross-link-${c.slug}`}
              >
                Property in {c.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Related city guide — surfaces the AI-drafted long-form guide once published */}
      {guide && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-12">
          <Link
            to={`/blog/${guide.slug}`}
            className="block bg-gradient-to-br from-[#FFF7ED] to-white border-l-4 border-[#E07A3A] p-6 sm:p-8 hover:shadow-lg transition-shadow group"
            data-testid="related-city-guide"
          >
            <div className="flex items-start gap-3 mb-3">
              <Sparkles className="h-4 w-4 text-[#E07A3A] mt-0.5 flex-shrink-0" />
              <p className="text-[11px] tracking-[0.3em] uppercase text-[#C4652E] font-bold">Read the full guide</p>
            </div>
            <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl text-[#0F172A] mb-2 group-hover:text-[#E07A3A] transition-colors">
              {guide.title}
            </h3>
            <p className="text-sm text-[#475569] leading-relaxed line-clamp-2">{guide.excerpt}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-[#E07A3A] text-sm tracking-widest uppercase font-semibold">
              Open guide <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </Link>
        </section>
      )}

      {/* FAQ — fuels FAQPage rich snippet */}
      <section className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 pb-20">
        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A] mb-7">{city.name} property — frequently asked</h2>
        <div className="space-y-3">
          {faqItems.map((q, i) => (
            <details key={q.q || `loc-faq-${i}`} className="group bg-white border border-[#E2E8F0] p-5">
              <summary className="cursor-pointer font-semibold text-[#0F172A] flex justify-between items-center">
                {q.q}
                <span className="text-[#E07A3A] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-[#475569] leading-relaxed">{q.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-[#FFF7ED] flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-[#E07A3A]" />
    </div>
    <div className="leading-tight">
      <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">{label}</p>
      <p className="text-sm font-bold text-[#0F172A]">{value}</p>
    </div>
  </div>
);

export default LocationLandingPage;
